(() => {
    const scriptEl = document.currentScript;
    const sectionEl = scriptEl && scriptEl.closest('.shopify-section');

    if (!sectionEl) return;
    if (sectionEl.dataset.artistSiteHeaderInitialized === 'true') return;
    sectionEl.dataset.artistSiteHeaderInitialized = 'true';

    const root = document.documentElement;
    const headerSection = sectionEl.querySelector('[data-artist-site-header-section]');
    const announcement = sectionEl.querySelector('[data-artist-announcement]');
    const header = sectionEl.querySelector('[data-scroll-header]');
    const toggle = sectionEl.querySelector('.artist-header__toggle');
    const drawer = sectionEl.querySelector('.artist-header__mobile-drawer');
    const closeBtn = sectionEl.querySelector('.artist-header__close');
    const backdrop = sectionEl.querySelector('[data-mobile-backdrop]');
    const mobileCartLink = sectionEl.querySelector('[data-artist-mobile-cart-link]');
    const desktopCartCountNodes = sectionEl.querySelectorAll('[data-artist-cart-count]');
    const desktopCartA11yLabel = sectionEl.querySelector('.artist-header__icon-link[href*="/cart"] .visually-hidden');

    if (!headerSection || !header) return;

    const SCROLL_TOP_THRESHOLD = 10;
    const SCROLL_DELTA_THRESHOLD = 6;

    let ticking = false;
    let resizeObserver = null;
    let rafId = null;
    let lastY = Math.max(window.scrollY || window.pageYOffset || 0, 0);

    let cartRequestId = 0;
    let cartAbortController = null;

    const getScrollY = () => Math.max(window.scrollY || window.pageYOffset || 0, 0);

    const setOffsets = () => {
      const announcementHeight = announcement ? Number(announcement.getBoundingClientRect().height.toFixed(2)) : 0;
      const headerHeight = header ? Number(header.getBoundingClientRect().height.toFixed(2)) : 0;
      const overlap = announcementHeight > 0 ? 1 : 0;
      const fixedTop = Math.max(0, announcementHeight + headerHeight - overlap);

      headerSection.style.setProperty('--artist-announcement-height', `${announcementHeight}px`);
      headerSection.style.setProperty('--artist-header-height', `${headerHeight}px`);
      headerSection.style.setProperty('--artist-fixed-top', `${fixedTop}px`);
    };

    const renderArtistCartCount = (count) => {
      desktopCartCountNodes.forEach((node) => {
        node.textContent = count;
        node.classList.toggle('is-hidden', count === 0);
      });

      if (mobileCartLink) {
        mobileCartLink.textContent = count > 0 ? `Cart (${count})` : 'Cart';
      }

      if (desktopCartA11yLabel) {
        desktopCartA11yLabel.textContent = `Cart, ${count} item${count !== 1 ? 's' : ''}`;
      }
    };

    const updateArtistCartCount = async () => {
      const requestId = ++cartRequestId;

      if (cartAbortController) {
        cartAbortController.abort();
      }

      cartAbortController = new AbortController();

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          signal: cartAbortController.signal
        });

        if (!response.ok) {
          throw new Error(`Cart request failed with status ${response.status}`);
        }

        const cart = await response.json();

        if (requestId !== cartRequestId) return;

        renderArtistCartCount(Number(cart.item_count) || 0);
      } catch (error) {
        if (error.name === 'AbortError') return;
      }
    };

    const isMenuOpen = () => !!(drawer && !drawer.hidden);

    const showHeader = () => {
      headerSection.classList.remove('is-hidden-on-scroll');
    };

    const hideHeader = () => {
      if (isMenuOpen()) return;
      headerSection.classList.add('is-hidden-on-scroll');
    };

    const syncScrolledState = () => {
      const currentY = getScrollY();
      const delta = currentY - lastY;

      header.classList.toggle('is-scrolled', currentY > SCROLL_TOP_THRESHOLD);

      if (currentY <= SCROLL_TOP_THRESHOLD) {
        showHeader();
      } else if (delta > SCROLL_DELTA_THRESHOLD) {
        hideHeader();
      } else if (delta < -SCROLL_DELTA_THRESHOLD) {
        showHeader();
      }

      lastY = currentY;
      ticking = false;
      rafId = null;
    };

    const requestScrollSync = () => {
      if (ticking) return;
      ticking = true;
      rafId = window.requestAnimationFrame(syncScrolledState);
    };

    const openMenu = () => {
      if (!drawer || !toggle) return;

      drawer.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      root.classList.add('artist-menu-open');

      showHeader();
      setOffsets();
      updateArtistCartCount();
    };

    const closeMenu = () => {
      if (!drawer || !toggle) return;

      drawer.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      root.classList.remove('artist-menu-open');

      requestScrollSync();
    };

    const handleResize = () => {
      setOffsets();
      requestScrollSync();
    };

    const handleLoadLikeEvent = (event) => {
      setOffsets();
      lastY = getScrollY();
      syncScrolledState();

      if (event && event.type === 'pageshow' && event.persisted) {
        updateArtistCartCount();
      }
    };

    const handleSectionRefresh = () => {
      setOffsets();
      lastY = getScrollY();
      syncScrolledState();
      updateArtistCartCount();
    };

    const bindMenuEvents = () => {
      if (!toggle || !drawer) return;

      toggle.addEventListener('click', () => {
        if (isMenuOpen()) {
          closeMenu();
        } else {
          openMenu();
        }
      });

      if (closeBtn) {
        closeBtn.addEventListener('click', closeMenu);
      }

      if (backdrop) {
        backdrop.addEventListener('click', closeMenu);
      }

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && isMenuOpen()) {
          closeMenu();
        }
      });

      drawer.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', closeMenu);
      });
    };

    const bindCartSyncEvents = () => {
      document.addEventListener('cart:refresh', updateArtistCartCount);
      document.addEventListener('cart:open', updateArtistCartCount);
      document.addEventListener('cart:updated', updateArtistCartCount);
      document.addEventListener('ajaxProduct:added', updateArtistCartCount);

      document.addEventListener('change', (event) => {
        const target = event.target;
        if (!target) return;

        if (
          target.matches('cart-items input[name="updates[]"]') ||
          target.matches('input[name="updates[]"]') ||
          target.matches('cart-remove-button button')
        ) {
          updateArtistCartCount();
        }
      });
    };

    setOffsets();
    syncScrolledState();

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => {
        setOffsets();
        requestScrollSync();
      });

      resizeObserver.observe(header);

      if (announcement) {
        resizeObserver.observe(announcement);
      }
    }

    window.addEventListener('load', handleLoadLikeEvent);
    window.addEventListener('pageshow', handleLoadLikeEvent);
    window.addEventListener('resize', handleResize);

    window.addEventListener(
      'scroll',
      () => {
        requestScrollSync();
      },
      { passive: true }
    );

    bindMenuEvents();
    bindCartSyncEvents();

    document.addEventListener('shopify:section:load', (event) => {
      if (event.target !== sectionEl) return;
      handleSectionRefresh();
    });

    document.addEventListener('shopify:section:reorder', handleSectionRefresh);

    document.addEventListener('shopify:section:select', (event) => {
      if (event.target !== sectionEl) return;
      showHeader();
      setOffsets();
      updateArtistCartCount();
    });

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target !== sectionEl) return;

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }

      if (cartAbortController) {
        cartAbortController.abort();
      }

      root.classList.remove('artist-menu-open');
      showHeader();
    });
  })();
